/**
 * useChoresWS — subscribe to topic 'chores' and invalidate TanStack Query.
 *
 * Plan 02-09 Task 3 / INV-WS-2 / Plan 02-09 Pattern 7 (a plan-research
 * pattern, NOT docs/ARCHITECTURE.md Pattern 7 — that number now belongs to
 * the stacking ladder, issue #45).
 *
 * Server (Plan 02-02 chores.subscribers) broadcasts verb-only payloads
 * over the /ws hub:
 *   { topic:'chores', event:'status_changed' | 'spawned' | 'expired' | 'sla_count_changed', data:{...} }
 *
 * The hook translates each verb into a `queryClient.invalidateQueries`
 * call so consumers (PoolBoardRoute, ApprovalsRoute, MyChoresStrip) see
 * the latest server state without explicit polling. The cache is the
 * authoritative store — WS events NEVER patch row data directly
 * (T-02-31 mitigation: prevent cache poisoning by other tabs/clients).
 *
 * Gjenoppkobling og backoff: se lib/reconnectingSocket.ts.
 *
 * The WS upgrade requires the same session cookie as /api/* routes.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

export function useChoresWS(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: unknown;
        };
        if (msg?.topic !== 'chores') return;
        switch (msg.event) {
          case 'status_changed':
          case 'spawned':
          case 'expired':
            // ['chores'] prefix-matches ['chores','board'] — one call covers
            // the pool feed and the unified board feed.
            void qc.invalidateQueries({ queryKey: ['chores'] });
            void qc.invalidateQueries({ queryKey: ['approvals'] });
            // Wave 4: an approval moves a row out of the queue and into the
            // Ferdig tab, so the history feed is stale on the same verb.
            void qc.invalidateQueries({ queryKey: ['chores-completed'] });
            // Fase 4: homework shares the chore lifecycle (seed/claim/complete/
            // approve all flow through chore_instances + the 'chores' topic).
            void qc.invalidateQueries({ queryKey: ['homework'] });
            break;
          case 'proposal_changed':
            // Fase 5: a kid suggestion was created/approved/rejected. Refresh
            // the admin review queue + the template list (an approval promotes
            // a proposal into a live template).
            void qc.invalidateQueries({ queryKey: ['chore-proposals'] });
            void qc.invalidateQueries({ queryKey: ['admin', 'chore-templates'] });
            break;
          case 'sla_count_changed':
            void qc.invalidateQueries({ queryKey: ['sla-count'] });
            break;
          case 'streak_risk_member':
            void qc.invalidateQueries({ queryKey: ['streak-risk'] });
            break;
          default:
            // Unknown verb — ignore silently. Server-validated envelopes
            // won't reach here; this is defence-in-depth for forward
            // compatibility (a new verb arriving against an old client).
            break;
        }
      } catch {
        // Malformed frame — drop silently per Plan 1 P15 pattern.
      }
    };

    return openReconnectingSocket({ topics: ['chores'], onMessage });
  }, [qc]);
}
